import {
    useCallback,
    useEffect,
} from 'react'

import ChipSelect from 'app/shared-components/ChipSelect'

import {
    ActionLinkFunction,
    Validate,
} from 'app/types/form.types'
import {
    Contact,
} from 'app/Apps/ContactManagement/Contacts/Contacts.types'

import useActionLinkFunction from 'app/hooks/useActionLinkFunction'
import useValidateItem from '../../hooks/useValidateItem'

import {
    valueConverterToArray,
    valueConverterToObject,
} from './utils/converters'
import getValueLabelByEmail from './utils/getValueLabelByEmail'
import useCommonMultiContactSelectorLogic, {
    Value,
} from './hooks/useCommonMultiContactSelectorLogic'

type Props = {
    name?: string,
    value?: Value[],
    onChange: (newValue: Value[]) => void,
    includeFilters?: Record<string, string[]>,
    filters?: Record<string, string[]>,
    getValueLabel?: (val: Record<string, any>) => string,
    title?: string,
    className?: string,
    disabled?: boolean,
    required?: boolean,
    validate?: Validate<Contact[]>,
    setErrors?: (e: [string, any[]]) => void,
    disableCache?: boolean,
    actionLinkFunction?: ActionLinkFunction<Contact[]>
}

const defaultProps: Partial<Props> = {
    value: [],
    title: 'Contacts',
    name: undefined,
    disabled: false,
    required: false,
    className: '',
    includeFilters: undefined,
    getValueLabel: undefined,
    validate: undefined,
    setErrors: undefined,
    filters: undefined,
    disableCache: false,
    actionLinkFunction: undefined,
}

export default function MultipleContactObjectSelector({
    name,
    value,
    onChange,
    title,
    disabled,
    required,
    className,
    includeFilters,
    getValueLabel,
    validate,
    setErrors,
    filters,
    disableCache,
    actionLinkFunction,
}: Readonly<Props>) {
    const {
        contacts,
        setContacts,
        loadOptions,
        contactsByIds,
    } = useCommonMultiContactSelectorLogic({
        valueConverter: valueConverterToArray,
        includeFilters,
        filters,
        listIds: value,
    })

    useEffect(() => {
        if (contactsByIds && JSON.stringify(contactsByIds) !== JSON.stringify(contacts)) {
            setContacts(contactsByIds)
        }
    }, [
        contacts,
        contactsByIds,
        setContacts,
    ])

    const onChangeValue = useCallback((newValue: Value[], actionParams): void => {
        let updatedValues

        if (actionParams?.action === 'remove-value') {
            updatedValues = contacts.filter(({
                id,
            }) => {
                return id !== actionParams?.removedValue?.id
            })
        } else {
            updatedValues = newValue
        }

        onChange(updatedValues ? valueConverterToObject(updatedValues) : [])
    }, [
        contacts,
        onChange,
    ])

    const {
        errors,
        validated,
    } = useValidateItem({
        validate, value: contacts, setFormError: setErrors, key: name,
    })

    const handleActionLinkFunction = useActionLinkFunction<Contact[]>({
        actionLinkFunction, value: contacts as Contact[],
    })

    return (
        <div className={className}>
            <ChipSelect
                title={title}
                value={contacts}
                onChange={onChangeValue}
                loadOptions={loadOptions}
                getValueLabel={getValueLabel || getValueLabelByEmail}
                showTitleOptions
                disabled={disabled}
                required={required}
                errors={errors}
                validated={validated}
                cacheOptions={!disableCache}
                menuShouldBlockScroll
                menuPortalTarget={document.body}
                actionLinkFunction={handleActionLinkFunction}
            />
        </div>
    )
}

MultipleContactObjectSelector.defaultProps = defaultProps
